<template>
  <b-card>
    <div class="p-1">
      <h4 class="mb-2">
        Rechercher un colis par bordereau
      </h4>

      <!-- Search Interface -->
      <b-form @submit.prevent="searchOrder">
        <b-row align-v="center">
          <b-col
            v-if="['admin', 'superadmin', 'admin_developer'].includes(userRole)"
            md="4"
          >
            <b-form-group
              label="Dépôt"
              label-for="repository-select"
            >
              <v-select
                id="repository-select"
                v-model="selectedRepo"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="repositories"
                label="name"
                placeholder="Sélectionner un dépôt"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Numéro du bordereau"
              label-for="search-bl"
            >
              <b-input-group>
                <b-form-input
                  id="search-bl"
                  v-model="searchBl"
                  placeholder="Ex: TPE123456"
                  required
                  autofocus
                />
                <b-input-group-append>
                  <b-button
                    type="submit"
                    variant="primary"
                    :disabled="searching"
                  >
                    <b-spinner
                      v-if="searching"
                      small
                      class="mr-50"
                    />
                    Rechercher
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <hr class="my-2">

      <!-- Result/Affichage Area -->
      <div
        v-if="order"
        class="order-result-area"
      >
        <b-row>
          <b-col md="6">
            <b-card
              border-variant="primary"
              class="shadow-none bg-light"
            >
              <h5 class="text-primary mb-1">
                Informations du colis
              </h5>
              <b-row class="mb-50">
                <b-col
                  cols="5"
                  class="font-weight-bold"
                >
                  N° Bordereau :
                </b-col>
                <b-col cols="7">
                  {{ order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : order.id }}
                </b-col>
              </b-row>
              <b-row class="mb-50">
                <b-col
                  cols="5"
                  class="font-weight-bold"
                >
                  Destinataire :
                </b-col>
                <b-col cols="7">
                  {{ order.name }}
                </b-col>
              </b-row>
              <b-row class="mb-50">
                <b-col
                  cols="5"
                  class="font-weight-bold"
                >
                  Statut :
                </b-col>
                <b-col cols="7">
                  <b-badge variant="light-primary">
                    {{ order.status_display }}
                  </b-badge>
                </b-col>
              </b-row>
              <b-row class="mb-50 text-success font-weight-bolder h5 mt-1">
                <b-col cols="5">
                  Frais Livraison Actuel :
                </b-col>
                <b-col cols="7">
                  {{ formatPrice(order.price_delivery) }} DT
                </b-col>
              </b-row>
              <div
                v-if="order.has_extra_fee"
                class="mt-1"
              >
                <small class="text-info italic">
                  * Ce colis a déjà des frais extra ajoutés.
                </small>
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 ml-1"
                  @click="viewHistory"
                >
                  Voir l'historique
                </b-button>
              </div>
            </b-card>
          </b-col>

          <b-col md="6">
            <b-card
              border-variant="success"
              class="shadow-none"
            >
              <h5 class="text-success mb-1">
                Ajouter des frais supplémentaires
              </h5>
              <b-form @submit.prevent="confirmSubmit">
                <b-form-group
                  label="Montant Extra à ajouter (DT)"
                  label-for="extra-amount"
                >
                  <b-form-input
                    id="extra-amount"
                    v-model="form.extra_amount"
                    type="number"
                    step="0.001"
                    min="0.001"
                    required
                    placeholder="Ex: 5.000"
                  />
                </b-form-group>

                <b-form-group
                  label="Raison / Commentaire"
                  label-for="extra-reason"
                >
                  <b-form-textarea
                    id="extra-reason"
                    v-model="form.reason"
                    placeholder="Pourquoi ajoutez-vous ces frais ?"
                    rows="3"
                    max-length="500"
                  />
                </b-form-group>

                <!-- Calculation Preview -->
                <div
                  v-if="form.extra_amount > 0"
                  class="alert alert-info py-1 mb-2"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <span>Nouveau Total Frais Livraison:</span>
                    <span class="font-weight-bolder h5 mb-0">
                      {{ formatPrice(newTotal) }} DT
                    </span>
                  </div>
                </div>

                <b-button
                  type="submit"
                  variant="success"
                  block
                  :disabled="submitLoading || !form.extra_amount || form.extra_amount <= 0"
                >
                  <b-spinner
                    v-if="submitLoading"
                    small
                    class="mr-50"
                  />
                  Confirmer et Enregistrer
                </b-button>
              </b-form>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- No result found / Placeholder -->
      <div
        v-else-if="!searching && searchBl"
        class="text-center py-5"
      >
        <feather-icon
          icon="SearchIcon"
          size="48"
          class="text-muted mb-1"
        />
        <p class="text-muted">
          Aucun colis trouvé avec ce numéro de bordereau ou non éligible (ne doit pas être "Livré payé", "Retour payé" ou "Retour expéditeur").
        </p>
      </div>
      <div
        v-else-if="!searching"
        class="text-center py-5"
      >
        <feather-icon
          icon="PackageIcon"
          size="48"
          class="text-muted mb-1"
        />
        <p class="text-muted">
          Entrez un numéro de bordereau pour commencer.
        </p>
      </div>
    </div>

    <!-- History Modal reuse -->
    <extra-fee-history-modal ref="extraFeeHistoryModal" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormGroup, BFormInput, BButton,
  BInputGroup, BInputGroupAppend, BSpinner, BBadge, BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import ExtraFeeHistoryModal from '../../components/orders/ExtraFeeHistoryModal.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BBadge,
    BFormTextarea,
    vSelect,
    ExtraFeeHistoryModal,
  },
  data() {
    return {
      searchBl: '',
      searching: false,
      order: null,
      submitLoading: false,
      form: {
        extra_amount: '',
        reason: '',
      },
      repositories: [],
      selectedRepo: null,
    }
  },
  computed: {
    user() {
      return storeAuth.state.user || {}
    },
    userRole() {
      // Try multiple sources for the role to be as robust as possible
      const role = storeAuth.getters.userRole || storeAuth.state.role || storeAuth.state.user?.role || localStorage.getItem('role') || ''
      return role.toLowerCase()
    },
    newTotal() {
      if (!this.order) return 0
      const current = parseFloat(this.order.price_delivery) || 0
      const extra = parseFloat(this.form.extra_amount) || 0
      return current + extra
    },
  },
  watch: {
    // Watch role to fetch repositories if we become admin/superadmin
    userRole: {
      immediate: true,
      handler(newRole) {
        if (['admin', 'superadmin', 'admin_developer'].includes(newRole)) {
          this.getRepositories()
        }
      },
    },
  },
  methods: {
    ...mapActions('extraFees', ['fetchEligibleOrders', 'addExtraFee']),
    formatPrice(value) {
      if (!value) return '0.000'
      return parseFloat(value).toFixed(3)
    },
    async searchOrder() {
      if (!this.searchBl) return

      this.searching = true
      this.order = null

      try {
        const params = {
          bl: this.searchBl.trim(),
        }
        if (this.selectedRepo && this.selectedRepo.id) {
          params.repository = this.selectedRepo.id
        }

        const response = await this.fetchEligibleOrders(params)
        const results = response.data.results || response.data
        if (Array.isArray(results) && results.length > 0) {
          [this.order] = results
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Non trouvé',
              icon: 'AlertCircleIcon',
              variant: 'warning',
              text: 'Colis non trouvé ou non éligible aux frais extra.',
            },
          })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Erreur lors de la recherche.',
          },
        })
      } finally {
        this.searching = false
      }
    },
    confirmSubmit() {
      this.$bvModal
        .msgBoxConfirm(`Voulez-vous ajouter ${this.formatPrice(this.form.extra_amount)} DT au colis ${this.order.id} ?`, {
          title: 'Confirmation de l\'ajout',
          okTitle: 'Confirmer',
          cancelTitle: 'Annuler',
          okVariant: 'success',
        })
        .then(value => {
          if (value) {
            this.executeAdd()
          }
        })
    },
    async executeAdd() {
      this.submitLoading = true
      const payload = {
        order_id: this.order.id,
        extra_amount: this.form.extra_amount,
        reason: this.form.reason,
      }

      try {
        await this.addExtraFee(payload)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Succès',
            icon: 'CheckIcon',
            variant: 'success',
            text: `Frais extra ajoutés avec succès pour le colis ${this.order.id}.`,
          },
        })

        // Refresh order data to show updated fee
        await this.searchOrder()

        // Reset form
        this.form.extra_amount = ''
        this.form.reason = ''
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.detail || 'Une erreur est survenue lors de l\'ajout.',
          },
        })
      } finally {
        this.submitLoading = false
      }
    },
    viewHistory() {
      if (this.order) {
        this.$refs.extraFeeHistoryModal.open(this.order.id)
      }
    },
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data.results || data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.order-result-area {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
